import { withOpacity } from '../../../utils/colorUtils';
import { ViewerModalFrame, ViewerModalSection, type ViewerModalTheme } from './ViewerModalFrame';

export interface TutorialStep {
  title: string;
  description: string;
  animation?: 'rotate' | 'zoom' | 'highlight';
}

interface ViewerTutorialModalProps {
  open: boolean;
  onClose: () => void;
  steps: TutorialStep[];
  title?: string;
  theme?: ViewerModalTheme;
}

const ANIMATION_ICONS: Record<string, { icon: string; colorKey: 'accent' | 'purple' | 'amber' }> = {
  rotate: { icon: '360', colorKey: 'accent' },
  zoom: { icon: 'zoom_in', colorKey: 'purple' },
  highlight: { icon: 'touch_app', colorKey: 'amber' },
};

export function ViewerTutorialModal({
  open,
  onClose,
  steps,
  title = 'How to Use 3D Manual',
  theme,
}: ViewerTutorialModalProps) {
  const t = {
    primaryTextColor: 'rgba(255, 255, 255, 0.95)',
    secondaryTextColor: 'rgba(255, 255, 255, 0.7)',
    accentColor: 'rgb(56, 189, 248)',
    borderColor: 'rgba(255, 255, 255, 0.1)',
    ...theme,
  };

  return (
    <ViewerModalFrame open={open} onClose={onClose} title={title} titleIcon="school" theme={theme}>
      <ViewerModalSection title="Getting started" theme={theme}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {steps.map((step, index) => {
            const animConfig = step.animation ? ANIMATION_ICONS[step.animation] : null;

            return (
              <div
                key={index}
                style={{
                  border: `1px solid ${t.borderColor}`,
                  borderRadius: '0.75rem',
                  overflow: 'hidden',
                  background: 'rgba(255, 255, 255, 0.02)',
                }}
              >
                <div
                  style={{
                    height: '4.5rem',
                    background: `linear-gradient(135deg, ${withOpacity(t.accentColor, 0.08)}, rgba(139, 92, 246, 0.08))`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <span
                    className="material-symbols-outlined"
                    style={{
                      fontSize: '1.5rem',
                      color: animConfig?.colorKey === 'accent'
                        ? withOpacity(t.accentColor, 0.8)
                        : animConfig?.colorKey === 'purple'
                          ? 'rgba(139, 92, 246, 0.8)'
                          : animConfig?.colorKey === 'amber'
                            ? 'rgba(251, 191, 36, 0.8)'
                            : 'rgba(255, 255, 255, 0.4)',
                      fontWeight: 300,
                    }}
                  >
                    {animConfig?.icon ?? 'school'}
                  </span>
                </div>
                <div style={{ padding: '0.75rem' }}>
                  <h4 style={{ fontWeight: 500, color: t.primaryTextColor, fontSize: '0.8125rem', margin: '0 0 0.25rem' }}>
                    {step.title}
                  </h4>
                  <p style={{ fontSize: '0.75rem', color: t.secondaryTextColor, margin: 0 }}>
                    {step.description}
                  </p>
                </div>
              </div>
            );
          })}
          {steps.length === 0 && (
            <p style={{ fontSize: '0.8125rem', color: t.secondaryTextColor, padding: '1.5rem 0.5rem', textAlign: 'center' }}>
              No tutorial steps available.
            </p>
          )}
        </div>
      </ViewerModalSection>
    </ViewerModalFrame>
  );
}
